// Educational blog posts for MD Supply Wellness
// Structured content blocks for consistent, SEO/AEO-friendly rendering.
// Each post is dated between Sept 1 – Sept 16, 2025, spread evenly for a
// natural publishing cadence.

import { IMAGES } from "@/lib/constants";

export const BLOG_POSTS = [
  // ------------------------------------------------------------------
  {
    slug: "cannabis-delivery-bethesda-md-what-to-know",
    title:
      "Cannabis Delivery in Bethesda, MD: What Adults 21+ Need to Know Before You Order",
    excerpt:
      "How adult-use and medical cannabis delivery actually works in Bethesda and Montgomery County — the rules, the ID checks, and what to expect from a licensed Maryland dispensary.",
    date: "2025-09-01",
    readingTime: "6 min",
    tags: ["Delivery", "Maryland Cannabis", "Bethesda"],
    image: IMAGES.macro1,
    keywords: [
      "cannabis delivery Bethesda MD",
      "weed delivery Bethesda",
      "Montgomery County dispensary delivery",
      "adult-use cannabis Maryland",
      "medical marijuana delivery Maryland",
    ],
    content: [
      {
        type: "p",
        text: "Cannabis delivery in Bethesda, Maryland is officially part of everyday life. Since Maryland launched adult-use sales on July 1, 2023, licensed dispensaries can deliver both medical and adult-use products directly to qualifying homes across Montgomery County. If you live near downtown Bethesda, Chevy Chase, Rockville, Potomac, Kensington or Silver Spring, chances are you're already inside a legal delivery zone — including the ZIP codes MD Supply Wellness serves from our 4713 Maple Ave location.",
      },
      {
        type: "p",
        text: "But delivery isn't a free-for-all. Maryland's rules are strict, and knowing them ahead of time makes ordering faster, safer, and cheaper.",
      },
      {
        type: "h2",
        text: "Who can legally order cannabis delivery in Maryland?",
      },
      {
        type: "ul",
        items: [
          "Any adult 21 or older with a valid, non-expired government-issued ID.",
          "Registered Maryland medical cannabis patients 18+ with a valid MMCC patient ID and matching government ID.",
          "The person who placed the order must personally accept the delivery — no leaving packages on porches, no passing to a roommate.",
        ],
      },
      {
        type: "h2",
        text: "What actually happens when you order delivery",
      },
      {
        type: "ol",
        items: [
          "You place an order online or over the phone with a licensed Maryland dispensary. Age and identity are verified up front.",
          "A trained delivery agent (an employee of the dispensary, not a third-party gig worker) prepares your order in tamper-evident packaging.",
          "At the door, the agent scans your ID again and confirms the order matches. You sign for it — that's the state's chain-of-custody requirement.",
          "Products stay sealed until they're inside your home. You cannot open or consume anything in the vehicle or in public.",
        ],
      },
      {
        type: "h2",
        text: "How far can Bethesda dispensaries deliver?",
      },
      {
        type: "p",
        text: "Maryland does not cap the delivery radius by mileage the way some states do — instead, it's driven by county coverage, licensing, and each dispensary's operational footprint. From our flagship on Maple Avenue we plan to serve, at minimum, these ZIP codes:",
      },
      {
        type: "ul",
        items: [
          "Bethesda: 20814, 20815, 20816, 20817",
          "Chevy Chase: 20815, 20825",
          "Rockville: 20850, 20852, 20853",
          "Potomac: 20854",
          "Kensington: 20895",
          "Silver Spring: 20910, 20901",
          "Neighboring DC border ZIPs: 20015, 20016 (where locally permitted)",
        ],
      },
      {
        type: "callout",
        text: "Not sure your ZIP is covered? Check our Areas Served page and enter your ZIP — every eligible ZIP has its own local delivery page with details.",
      },
      {
        type: "h2",
        text: "What can (and can't) go in a delivery order",
      },
      {
        type: "p",
        text: "Maryland allows adults 21+ to purchase up to the state's personal-use limit per day, which currently equals 1.5 ounces of flower (or the THC-equivalent in concentrate, edibles, or vape form). Medical patients have higher allowances tied to their MMCC certification. Every product on a compliant delivery order must:",
      },
      {
        type: "ul",
        items: [
          "Come from a Maryland-licensed grower or processor.",
          "Include a batch-level Certificate of Analysis (COA) showing potency and contaminant testing.",
          "Be sealed in child-resistant, tamper-evident packaging.",
          "Carry the required Maryland warning labels.",
        ],
      },
      {
        type: "h2",
        text: "Frequently asked questions about Bethesda cannabis delivery",
      },
      {
        type: "faq",
        items: [
          {
            q: "Do I have to be home for a cannabis delivery in Bethesda?",
            a: "Yes. The person who placed the order — the same name and ID used at checkout — must personally receive the package and sign for it. Deliveries cannot be left unattended in Maryland.",
          },
          {
            q: "Can I pay cash for cannabis delivery?",
            a: "Most Maryland dispensaries accept cash and debit-style ATM payments at the door. Because cannabis is federally illegal, traditional credit-card processing is limited — call ahead if you have questions about payment.",
          },
          {
            q: "Is there a delivery minimum in Montgomery County?",
            a: "Delivery minimums vary by dispensary and by ZIP code. MD Supply Wellness will publish exact minimums and delivery windows on our Areas Served page at launch.",
          },
          {
            q: "How long does cannabis delivery take in Bethesda?",
            a: "Same-day delivery is standard for orders placed during business hours. Expect roughly 60–120 minutes from confirmation to doorstep for most Bethesda, Chevy Chase and Potomac addresses.",
          },
        ],
      },
      {
        type: "p",
        text: "Ready to be first in line? Join our waitlist and we'll notify you the moment adult-use and medical delivery from MD Supply Wellness goes live at 4713 Maple Ave in Bethesda.",
      },
    ],
  },

  // ------------------------------------------------------------------
  {
    slug: "maryland-medical-cannabis-vs-adult-use",
    title:
      "Medical Cannabis vs. Adult-Use in Maryland: What Actually Changed and What You Should Know",
    excerpt:
      "You can now buy cannabis in Maryland at 21 without a medical card — but should you? Here's a clear breakdown of the medical program, adult-use rules, taxes, and who benefits from each.",
    date: "2025-09-03",
    readingTime: "7 min",
    tags: ["Maryland Program", "Medical", "Adult Use"],
    image: IMAGES.macro2,
    keywords: [
      "Maryland medical cannabis card",
      "MMCC card benefits",
      "adult use vs medical Maryland",
      "recreational marijuana Maryland",
      "cannabis tax Maryland",
    ],
    content: [
      {
        type: "p",
        text: "When Maryland voters approved adult-use cannabis in November 2022, and sales officially opened on July 1, 2023, one of the most common questions we hear at MD Supply Wellness in Bethesda is: 'Do I even need my medical card anymore?' The honest answer is: it depends on how you use cannabis, how often, and how you like to pay.",
      },
      {
        type: "p",
        text: "Both programs are legal in Maryland. Both are regulated by the Maryland Cannabis Administration (MCA). But they serve different customers and come with meaningfully different rules.",
      },
      {
        type: "h2",
        text: "The quick comparison",
      },
      {
        type: "h3",
        text: "Adult-use cannabis in Maryland",
      },
      {
        type: "ul",
        items: [
          "Available to anyone 21 or older with a valid government ID.",
          "Personal-use limit: 1.5 ounces of flower per day, or its THC-equivalent in other forms.",
          "Standard 9% Maryland cannabis sales tax applies.",
          "No registration, no medical evaluation, no annual fees.",
          "Cannot be consumed in public, in vehicles, or on federal property.",
        ],
      },
      {
        type: "h3",
        text: "Maryland medical cannabis (MMCC-registered patients)",
      },
      {
        type: "ul",
        items: [
          "Available to Maryland residents 18+ (minors may qualify with a caregiver) with a state-issued MMCC patient card.",
          "Higher purchase and possession limits than adult-use.",
          "No sales tax on medical cannabis purchases.",
          "Broader access to product categories tailored to therapeutic use (higher CBD ratios, specific dose formats, etc.).",
          "Often prioritized during shortages and given access to patient-only inventory or discounts at many dispensaries.",
        ],
      },
      {
        type: "h2",
        text: "Should you still get a medical card?",
      },
      {
        type: "p",
        text: "For a lot of adult customers, the answer is 'no — adult-use is simpler.' But there are three clear cases where the medical program still wins:",
      },
      {
        type: "ol",
        items: [
          "You use cannabis regularly. The tax savings alone can pay for the annual MMCC fee within a few months.",
          "You need higher potency or larger quantities. Medical patients have larger monthly allowances than adult-use limits.",
          "You want clinical guidance. A certifying provider can advise on strain choice, dosing, and interactions with other medications.",
        ],
      },
      {
        type: "h2",
        text: "How to get a Maryland medical cannabis card",
      },
      {
        type: "ol",
        items: [
          "Register as a patient on the MCA online portal.",
          "Book a certification appointment with a Maryland-registered provider (many operate via telehealth).",
          "Once certified, purchase your patient ID card through the state portal (currently around $50 annually).",
          "Bring your MMCC ID plus a government ID to any Maryland dispensary, including MD Supply Wellness in Bethesda.",
        ],
      },
      {
        type: "h2",
        text: "What both programs share",
      },
      {
        type: "p",
        text: "Whether you buy under the medical or adult-use program at our Bethesda dispensary, the same core standards apply:",
      },
      {
        type: "ul",
        items: [
          "All products are sourced from Maryland-licensed growers and processors.",
          "Every batch is third-party lab tested for potency, pesticides, heavy metals, and microbial contaminants.",
          "Products are sold in child-resistant, tamper-evident packaging.",
          "On-site consumption is not permitted anywhere in Maryland.",
        ],
      },
      {
        type: "h2",
        text: "Frequently asked questions",
      },
      {
        type: "faq",
        items: [
          {
            q: "Do I need a Maryland medical card if I already have one from another state?",
            a: "Maryland does not currently offer reciprocity for out-of-state medical cards. Out-of-state patients 21+ can still purchase under the adult-use program with a valid government ID.",
          },
          {
            q: "How much does a Maryland medical marijuana card cost in 2025?",
            a: "The state patient ID card is currently $50 per year. Certification appointments with a provider typically range from $150 to $250 for the initial visit, with lower renewal fees.",
          },
          {
            q: "Are medical patients treated differently at a dispensary?",
            a: "Yes. Medical patients often get priority service, tax-free pricing, patient-only inventory, and higher purchase limits. Adult-use customers get access to the same quality standards but pay the 9% state cannabis tax.",
          },
          {
            q: "Can adult-use customers still buy CBD products in Maryland?",
            a: "Absolutely. Adult-use customers can purchase the full range of THC and CBD products available at licensed dispensaries — including balanced-ratio tinctures, high-CBD flower, and topicals.",
          },
        ],
      },
    ],
  },

  // ------------------------------------------------------------------
  {
    slug: "cannabis-terpenes-101-guide",
    title:
      "Cannabis Terpenes 101: Myrcene, Limonene, Pinene and the Flavors Behind the Effect",
    excerpt:
      "Terpenes are the aroma compounds that make one strain feel calming and another feel energizing. Here's how to read a terpene profile — and why it matters more than the strain name.",
    date: "2025-09-05",
    readingTime: "6 min",
    tags: ["Education", "Terpenes", "How-To"],
    image: IMAGES.macro1,
    keywords: [
      "cannabis terpenes explained",
      "myrcene effects",
      "limonene cannabis",
      "pinene strain",
      "terpene profile guide",
      "sativa vs indica terpenes",
    ],
    content: [
      {
        type: "p",
        text: "If you've ever cracked open a jar of cannabis flower and thought 'that smells like citrus, pine, or lavender' — you were smelling terpenes. Terpenes are the aromatic compounds produced by cannabis (and by many other plants — think pepper, mango, lemon peel, hops). They give each strain its distinctive smell, and they play a real role in how a strain feels.",
      },
      {
        type: "p",
        text: "In fact, forget 'indica' and 'sativa' for a minute. Modern cannabis science increasingly points to the combination of cannabinoids (THC, CBD, CBG, etc.) and the terpene profile as the real drivers of the effect. Two 'sativa' strains with different terpenes will feel very different.",
      },
      {
        type: "h2",
        text: "The six terpenes you'll see most often on a Maryland dispensary menu",
      },
      {
        type: "h3",
        text: "1. Myrcene — earthy, musky, mango",
      },
      {
        type: "p",
        text: "The most abundant terpene in commercial cannabis. Associated with a heavier body-forward feel — the classic 'couch-lock' quality is often tied to high-myrcene strains. Also found in mangoes, hops, and lemongrass.",
      },
      {
        type: "h3",
        text: "2. Limonene — bright citrus",
      },
      {
        type: "p",
        text: "Smells like lemon peel and orange rind. Frequently linked in preliminary research to mood elevation and stress relief. Common in strains marketed as uplifting or daytime-friendly.",
      },
      {
        type: "h3",
        text: "3. Pinene — fresh pine, rosemary",
      },
      {
        type: "p",
        text: "Two forms exist — alpha and beta pinene. Users often describe pinene-forward strains as clear-headed and focus-friendly. Also found in pine needles, basil, and rosemary.",
      },
      {
        type: "h3",
        text: "4. Caryophyllene — pepper, clove, warm spice",
      },
      {
        type: "p",
        text: "Unique among terpenes because it can bind directly to the body's CB2 cannabinoid receptors. Often associated with a calming, anti-inflammatory feel. If a strain smells peppery or spicy, caryophyllene is usually the reason.",
      },
      {
        type: "h3",
        text: "5. Linalool — floral, lavender",
      },
      {
        type: "p",
        text: "The same terpene that gives lavender its scent. Often described as relaxing, and commonly chosen by customers looking for a wind-down evening effect.",
      },
      {
        type: "h3",
        text: "6. Terpinolene — herbal, piney, slightly floral",
      },
      {
        type: "p",
        text: "Complex and hard to pin down — often described as fresh, uplifting, and 'brighter.' Common in classic Jack Herer-lineage strains.",
      },
      {
        type: "h2",
        text: "How to read a terpene profile on a product label",
      },
      {
        type: "p",
        text: "In Maryland, every regulated cannabis product should have a Certificate of Analysis (COA) — that's where terpene data lives. Look for:",
      },
      {
        type: "ul",
        items: [
          "Total terpene percentage — most quality flower falls between 1% and 3.5%. Higher generally means more expressive aroma and flavor.",
          "Top three terpenes — this tells you far more than 'indica' or 'sativa.' A myrcene-dominant flower will feel very different from a limonene-dominant flower.",
          "Batch-specific data — terpene levels can vary batch to batch even in the same strain, so always check the latest COA, not last year's marketing.",
        ],
      },
      {
        type: "callout",
        text: "Tip: If a strain you love isn't in stock, ask a budtender for something with a similar top-three terpene profile. You'll usually get closer to the same effect than by matching strain names.",
      },
      {
        type: "h2",
        text: "Terpenes FAQ",
      },
      {
        type: "faq",
        items: [
          {
            q: "Do terpenes get you high?",
            a: "No. Terpenes are not intoxicating on their own. They shape how the cannabinoids in the plant feel, but they do not produce a 'high' without THC.",
          },
          {
            q: "What's the entourage effect?",
            a: "The entourage effect is the theory that cannabinoids and terpenes work better together than in isolation. It's why full-spectrum products — flower, live-resin vapes, whole-plant tinctures — often feel more nuanced than pure THC distillate.",
          },
          {
            q: "How should I store cannabis to preserve terpenes?",
            a: "Store cannabis in an airtight glass jar, out of direct light, at cool room temperature. Terpenes are volatile — heat, light, and oxygen degrade them fastest.",
          },
        ],
      },
    ],
  },

  // ------------------------------------------------------------------
  {
    slug: "how-to-read-cannabis-coa",
    title:
      "How to Read a Cannabis Certificate of Analysis (COA): A Buyer's Guide from a Bethesda Dispensary",
    excerpt:
      "A COA is a lab-tested proof sheet for what's actually in your cannabis. Here's exactly what to look for, what to skip, and how to spot a legitimate Maryland-tested product.",
    date: "2025-09-07",
    readingTime: "7 min",
    tags: ["Compliance", "Lab Testing", "Buyer's Guide"],
    image: IMAGES.macro2,
    keywords: [
      "cannabis certificate of analysis",
      "COA cannabis explained",
      "how to read cannabis lab test",
      "third-party cannabis testing Maryland",
      "cannabis potency test",
    ],
    content: [
      {
        type: "p",
        text: "A Certificate of Analysis — usually abbreviated as COA — is the lab report attached to every legal cannabis product in Maryland. It's your receipt that the flower, vape, edible, or tincture you're buying was tested by an independent, state-approved lab. And it's one of the single best ways to shop smarter at a dispensary.",
      },
      {
        type: "p",
        text: "At our Bethesda location, every product on the shelf is required to have a valid, batch-specific COA. Here's how to read one like a pro — and what to look for before you spend a dollar.",
      },
      {
        type: "h2",
        text: "What a Maryland-compliant COA must show",
      },
      {
        type: "ul",
        items: [
          "Product batch or lot number — links the report directly to what's in your hand.",
          "Name and license number of the testing laboratory.",
          "Date of collection and date of testing.",
          "Cannabinoid potency panel — THC, THCa, CBD, CBDa, CBG, CBN and more.",
          "Terpene profile — often the top 8–12 terpenes by percentage.",
          "Contaminant panels — pesticides, residual solvents, heavy metals, mycotoxins, microbials.",
          "Water activity / moisture content for flower — a freshness indicator.",
        ],
      },
      {
        type: "h2",
        text: "The single most important number: Total THC",
      },
      {
        type: "p",
        text: "When you see '24% THC' on a jar, that's usually 'Total THC' — a calculated number that accounts for how THCa converts to active THC when heated. The exact formula is: Total THC ≈ THCa × 0.877 + THC. Understanding this matters because raw flower is mostly THCa, and only turns into psychoactive THC when combusted or vaporized.",
      },
      {
        type: "callout",
        text: "Higher THC is not always better. A 22% flower with a rich terpene profile will often feel more nuanced than a 30% flower with almost no terpene expression.",
      },
      {
        type: "h2",
        text: "Contaminant panels — what should say 'Pass'",
      },
      {
        type: "ul",
        items: [
          "Pesticides — must fall under Maryland's action limits for each listed compound.",
          "Residual solvents (mostly relevant for concentrates/vapes) — must show no detectable butane, propane, or other extraction leftovers above threshold.",
          "Heavy metals — arsenic, cadmium, lead, mercury must be within safe limits.",
          "Microbials — no salmonella, no E. coli, no aspergillus above allowable counts.",
          "Mycotoxins — aflatoxin and ochratoxin, must pass.",
        ],
      },
      {
        type: "p",
        text: "If any category shows 'Fail,' the product legally cannot be sold in Maryland. Every legitimate dispensary — including MD Supply Wellness — should refuse it.",
      },
      {
        type: "h2",
        text: "Red flags on a cannabis COA",
      },
      {
        type: "ol",
        items: [
          "Missing batch number or lot ID.",
          "Testing lab is not licensed in the state of purchase.",
          "COA is more than 12 months old for flower or 24 months old for concentrates.",
          "'THC%' listed but no THCa or Total THC calculation shown.",
          "No terpene panel at all — a sign of a bare-minimum test.",
          "Suspiciously high potency (e.g., '35% THC flower') with no corroborating third-party test — this is a classic illicit market red flag.",
        ],
      },
      {
        type: "h2",
        text: "How to actually check a COA at the dispensary",
      },
      {
        type: "ol",
        items: [
          "Ask your budtender to see the COA — a good Maryland dispensary can pull it up on tablet or on the packaging QR code.",
          "Scan the QR code (if provided) and confirm the batch number matches your product.",
          "Skim the potency section and cross-check the label.",
          "Verify every contaminant panel says 'Pass' or 'ND' (Not Detected).",
        ],
      },
      {
        type: "h2",
        text: "COA FAQ",
      },
      {
        type: "faq",
        items: [
          {
            q: "Are all Maryland cannabis products tested by the same lab?",
            a: "No. Maryland licenses multiple independent third-party labs. Each lab reports results in a similar format, but their layouts differ. What matters is that the lab is state-approved and the batch numbers match.",
          },
          {
            q: "How fresh should a flower COA be?",
            a: "Cannabis flower is best consumed within 6–9 months of harvest. If the COA is more than a year old and the product is still on the shelf, ask why — it may still be safe, but potency and terpene levels will have dropped.",
          },
          {
            q: "Does a COA guarantee the product is organic?",
            a: "No. COAs verify safety and potency but do not certify organic growing methods. If organic or living-soil cultivation matters to you, ask your budtender about specific growers.",
          },
        ],
      },
    ],
  },

  // ------------------------------------------------------------------
  {
    slug: "cannabis-dosing-thc-cbd-guide",
    title:
      "Cannabis Dosing 101: Understanding THC, CBD, and How to Find Your Personal Threshold",
    excerpt:
      "There's no universal 'right dose' of cannabis. Here's how to think about milligrams, how long each product takes to hit, and the number-one rule for first-time customers.",
    date: "2025-09-10",
    readingTime: "8 min",
    tags: ["Dosing", "Beginner", "Health & Safety"],
    image: IMAGES.macro1,
    keywords: [
      "cannabis dosage",
      "edible dosing chart",
      "how much THC to take",
      "start low go slow cannabis",
      "microdosing cannabis",
      "CBD to THC ratio",
    ],
    content: [
      {
        type: "p",
        text: "Ask ten cannabis users what a 'normal' dose is and you'll get ten different answers. That's because dosing depends on your body chemistry, your tolerance, how much you've eaten, the product format, and even your stress levels that day. But there are ranges — and there's one rule everyone in Maryland's cannabis industry agrees on: start low, go slow.",
      },
      {
        type: "h2",
        text: "How cannabis dosing is measured",
      },
      {
        type: "p",
        text: "For edibles, tinctures, capsules, and drinks, dose is measured in milligrams (mg) of THC (and often CBD). For flower and vapes, dose is measured by the amount you inhale (a single puff typically delivers 2–5 mg of active THC, depending on the flower's potency). Understanding mg is the fastest way to have a predictable experience.",
      },
      {
        type: "h2",
        text: "Beginner-friendly starting doses (adults 21+, no tolerance)",
      },
      {
        type: "ul",
        items: [
          "Edibles / drinks: 2.5–5 mg THC. Wait at least 2 hours before considering more.",
          "Tinctures: 2.5–5 mg THC under the tongue. Onset 15–45 minutes.",
          "Flower or preroll: 1 small puff. Wait 10–15 minutes.",
          "Vape cartridge: 1 short 2-second inhale. Wait 10–15 minutes.",
        ],
      },
      {
        type: "callout",
        text: "The most common cause of an unpleasant cannabis experience — hands down — is taking a second edible dose before the first has finished absorbing. Wait the full two hours. Every time.",
      },
      {
        type: "h2",
        text: "Onset and duration by product type",
      },
      {
        type: "ul",
        items: [
          "Inhaled (flower / vape): onset 1–5 minutes, peak 15–30 minutes, duration 1–3 hours.",
          "Sublingual tincture (held under the tongue): onset 15–45 minutes, duration 3–6 hours.",
          "Edibles / capsules / drinks: onset 30 minutes – 2 hours, peak around 2–3 hours, duration 4–8 hours.",
          "Topicals: localized effect only, non-intoxicating, applied to skin.",
        ],
      },
      {
        type: "h2",
        text: "Why CBD matters even if you're chasing THC",
      },
      {
        type: "p",
        text: "CBD (cannabidiol) is non-intoxicating and, in many people, softens the sharper edges of THC — including anxiety and heart-rate spikes. Products marketed as 1:1 CBD:THC, 2:1, or 4:1 are often ideal for newcomers because they retain the therapeutic properties of cannabis while dialing back the intensity.",
      },
      {
        type: "h3",
        text: "Common ratios and who they're for",
      },
      {
        type: "ul",
        items: [
          "20:1 CBD:THC — daytime, near-zero head effect. Popular with new patients and people using cannabis for anti-inflammatory support.",
          "4:1 CBD:THC — mildly relaxing, minimal impairment. A common 'first step' into THC.",
          "1:1 CBD:THC — balanced, noticeable effect with a softer ceiling than pure THC.",
          "1:2 or higher THC ratio — full THC-forward experience.",
        ],
      },
      {
        type: "h2",
        text: "Building tolerance (and taking tolerance breaks)",
      },
      {
        type: "p",
        text: "Tolerance builds fast — often within a week or two of daily use. Users report needing 2–3x the initial dose after a month of consistent consumption. A 48-hour break usually noticeably resets sensitivity. A full 7-day break resets it substantially. This is a normal, reversible response.",
      },
      {
        type: "h2",
        text: "When to slow down or stop",
      },
      {
        type: "ul",
        items: [
          "You're feeling anxious, sweaty, or paranoid — hydrate, sit down, breathe. Sniffing whole peppercorns (yes, really — high in beta-caryophyllene) or taking pure CBD can help take the edge off.",
          "You're mixing with alcohol or other substances — combine effects can compound unpredictably.",
          "You're pregnant, breastfeeding, or under 21 without a medical card — cannabis is not appropriate.",
          "You're operating a vehicle — never drive impaired. Maryland enforces cannabis DUI laws.",
        ],
      },
      {
        type: "h2",
        text: "Dosing FAQ",
      },
      {
        type: "faq",
        items: [
          {
            q: "Can I overdose on cannabis?",
            a: "There are no known fatal cannabis overdoses in the medical literature — cannabis does not affect the brainstem receptors that control breathing. But you can absolutely take too much and have a genuinely unpleasant few hours. Start low.",
          },
          {
            q: "Why did my edible not work?",
            a: "The most common reasons are: (1) you didn't wait long enough — edibles can take up to 2 hours, (2) you took it on a full stomach and absorption slowed, or (3) about 20% of adults naturally metabolize edibles very slowly and feel very little from oral THC. Try a sublingual tincture instead.",
          },
          {
            q: "What's a microdose?",
            a: "Microdosing usually means 1–2.5 mg of THC per dose — enough to feel a subtle mood or focus shift, not enough to feel 'high.' It's a popular pattern for people using cannabis for wellness rather than recreation.",
          },
        ],
      },
    ],
  },

  // ------------------------------------------------------------------
  {
    slug: "cannabis-consumption-methods-guide",
    title:
      "Flower, Vape, Edible or Tincture? Choosing the Right Cannabis Consumption Method for You",
    excerpt:
      "The delivery method changes everything — onset time, duration, dose control, even flavor. Here's a plain-English comparison of the four main ways adults consume cannabis today.",
    date: "2025-09-12",
    readingTime: "7 min",
    tags: ["Consumption Methods", "Product Guide", "Beginner"],
    image: IMAGES.macro2,
    keywords: [
      "cannabis flower vs vape",
      "edibles vs tinctures",
      "best way to consume cannabis",
      "types of cannabis products",
      "cannabis vape safety",
    ],
    content: [
      {
        type: "p",
        text: "Walk into any Maryland dispensary — including MD Supply Wellness in Bethesda — and you'll see the shelves organized by format: flower, prerolls, vapes, edibles, tinctures, capsules, topicals, concentrates. Each format has real trade-offs. Choosing well is the difference between an experience you love and one you don't.",
      },
      {
        type: "h2",
        text: "Flower — the classic",
      },
      {
        type: "p",
        text: "Dried cannabis buds, usually smoked in a pipe, joint, preroll, or bong. Fastest onset (1–5 minutes) and shortest duration (1–3 hours). Total dose control is high — take one puff, wait, decide. Full-spectrum by nature: you get the full cannabinoid and terpene profile of the plant.",
      },
      {
        type: "ul",
        items: [
          "Best for: users who want fast onset and precise control.",
          "Watch out for: smoke irritation, smell, and the fact that you can't easily 'save' a preroll once it's lit.",
        ],
      },
      {
        type: "h2",
        text: "Vape cartridges — clean, discreet, portable",
      },
      {
        type: "p",
        text: "A 510-thread or proprietary battery paired with a cannabis oil cartridge. Faster onset than edibles, less smell than flower, and often the easiest option for travel. Quality varies dramatically — this is one category where the COA really matters.",
      },
      {
        type: "ul",
        items: [
          "Distillate carts: highest potency, most affordable, sometimes lack terpene complexity.",
          "Live-resin / live-rosin carts: preserve more of the plant's original terpenes and flavor. Cost more.",
          "Look for: no cutting agents (PG, VG, MCT), all-cannabis-derived terpenes, and a full contaminant panel on the COA.",
        ],
      },
      {
        type: "h2",
        text: "Edibles — long, strong, and easy to over-do",
      },
      {
        type: "p",
        text: "Gummies, chocolates, drinks, mints, baked goods. Slowest onset (30 minutes – 2 hours) and longest duration (4–8 hours). Precise mg-based dosing on every piece. Ideal for evenings, chronic pain, or sleep — and the format most likely to cause a bad experience if you dose impatiently.",
      },
      {
        type: "callout",
        text: "The single best piece of edibles advice: cut a 10 mg gummy in half, take 5 mg, and wait two full hours before adding more. This one habit prevents almost every 'I ate too much' story you've ever heard.",
      },
      {
        type: "h2",
        text: "Tinctures — the underrated middle ground",
      },
      {
        type: "p",
        text: "Alcohol- or MCT-based cannabis extracts, taken as drops under the tongue. Onset in 15–45 minutes (much faster than edibles), duration around 3–6 hours. Precise dosing by the milliliter or dropper. Non-inhaled, low-smell, discreet. Great for people who want repeatable, measured doses without smoking or waiting an hour on an edible.",
      },
      {
        type: "h2",
        text: "Topicals — cannabis without the high",
      },
      {
        type: "p",
        text: "Lotions, balms, and patches applied directly to skin. Non-intoxicating unless it's a transdermal patch (which does enter the bloodstream). Popular for localized muscle soreness, joint aches, and skin conditions. Won't show up in most standard drug tests when used non-transdermally.",
      },
      {
        type: "h2",
        text: "Quick chooser: match the method to the moment",
      },
      {
        type: "ul",
        items: [
          "Fast onset, short experience — flower or vape.",
          "Long, gradual evening — edible.",
          "Precise repeatable dose, discreet — tincture.",
          "Localized pain, no head effect — topical.",
          "Extreme tolerance / heavy use — concentrates (with caution).",
        ],
      },
      {
        type: "h2",
        text: "Consumption method FAQ",
      },
      {
        type: "faq",
        items: [
          {
            q: "Are cannabis vapes safe?",
            a: "Regulated Maryland cannabis vapes are tested for residual solvents, heavy metals, and cutting agents. The 2019 EVALI vape crisis was tied almost exclusively to unregulated market products containing vitamin E acetate. Buying from a licensed dispensary drastically reduces that risk — always check the COA.",
          },
          {
            q: "Do edibles hit harder than smoking?",
            a: "Yes, milligram for milligram. When cannabis is digested, THC is converted in the liver into 11-hydroxy-THC, which is more potent and longer-lasting than the delta-9 THC you inhale. That's why 5 mg edible feels different from a 5 mg puff.",
          },
          {
            q: "Which method smells the least?",
            a: "Tinctures, edibles, and topicals have essentially no odor. Vapes have a light, quickly-dissipating scent. Flower has the strongest and most persistent smell — worth considering if you live in shared housing.",
          },
        ],
      },
    ],
  },

  // ------------------------------------------------------------------
  {
    slug: "how-to-store-cannabis-properly",
    title:
      "How to Store Cannabis Properly: Preserving Potency, Terpenes and Freshness at Home",
    excerpt:
      "Cannabis is fresh produce, not a canned good. Store it wrong and you lose potency, flavor, and effect within weeks. Here's how to keep flower, edibles, and vapes at their best.",
    date: "2025-09-14",
    readingTime: "5 min",
    tags: ["Storage", "How-To", "At Home"],
    image: IMAGES.macro1,
    keywords: [
      "how to store cannabis",
      "how to keep weed fresh",
      "cannabis storage jar",
      "cannabis shelf life",
      "boveda humidity pack",
    ],
    content: [
      {
        type: "p",
        text: "Every cannabis product you buy at a Maryland dispensary has a clock on it. Terpenes evaporate. Cannabinoids oxidize (THC slowly turns into CBN over time, making flower feel sleepier and less bright). Moisture escapes. Store cannabis well and it stays fresh for 6–12 months. Store it badly and you can lose noticeable quality in as little as 4–6 weeks.",
      },
      {
        type: "h2",
        text: "The four enemies of cannabis freshness",
      },
      {
        type: "ol",
        items: [
          "Light — especially direct sunlight and UV. Degrades cannabinoids and terpenes.",
          "Heat — accelerates every chemical breakdown. Anything above ~70°F is not ideal.",
          "Oxygen — slowly oxidizes THC and volatile terpenes.",
          "Humidity swings — too dry (below ~55% RH) makes flower harsh; too wet (above ~65% RH) risks mold.",
        ],
      },
      {
        type: "h2",
        text: "How to store flower at home",
      },
      {
        type: "ul",
        items: [
          "Use an opaque or amber glass jar with an airtight seal (Mason jars work great).",
          "Add a two-way humidity pack (Boveda or Integra Boost, 58% or 62%) inside the jar.",
          "Keep the jar in a cool, dark cabinet — not on the kitchen counter, not on top of the fridge.",
          "Do not refrigerate flower. Fridge cycling causes condensation, which invites mold.",
          "Do not freeze flower unless it's for very long-term (6+ month) storage in a completely airtight container — and even then, thaw slowly.",
        ],
      },
      {
        type: "h2",
        text: "How to store vape cartridges",
      },
      {
        type: "ul",
        items: [
          "Stand cartridges upright, mouthpiece up, to keep oil in the base.",
          "Keep at cool room temperature — avoid parked cars, sunny windowsills, or heating vents.",
          "If oil separates or crystallizes in cold storage, warm the cart in a closed hand for a few minutes before use.",
        ],
      },
      {
        type: "h2",
        text: "How to store edibles",
      },
      {
        type: "ul",
        items: [
          "Follow the packaging — most gummies and chocolates are stable at room temperature.",
          "In hot months, refrigerate chocolate edibles to prevent melting and bloom.",
          "Keep edibles in a locked or high, out-of-reach location — this is Maryland law when you have children in the home.",
        ],
      },
      {
        type: "h2",
        text: "How to store tinctures",
      },
      {
        type: "ul",
        items: [
          "Store upright, cap tight, out of direct light.",
          "MCT-based tinctures can develop a slight sediment layer — shake before use.",
          "Alcohol-based tinctures are shelf-stable for 12+ months when sealed properly.",
        ],
      },
      {
        type: "h2",
        text: "Signs your cannabis is past its prime",
      },
      {
        type: "ul",
        items: [
          "Bone-dry flower that crumbles to dust — terpenes are gone, potency likely diminished.",
          "Faded, hay-like smell instead of the bright aroma you remember.",
          "Visible dark or fuzzy spots — do not consume. Discard immediately.",
          "Vape cartridge that has turned very dark — oxidation has occurred; flavor and potency will suffer.",
        ],
      },
      {
        type: "h2",
        text: "Storage FAQ",
      },
      {
        type: "faq",
        items: [
          {
            q: "How long does cannabis flower stay good?",
            a: "With proper storage — airtight glass, dark, cool, humidity-controlled — Maryland-grade flower stays at peak quality for about 6–9 months and remains safe to use for 12+ months, though terpenes and effect degrade over time.",
          },
          {
            q: "Should I keep cannabis in the plastic bag it came in?",
            a: "Only short-term. Plastic can react with terpenes and doesn't seal as reliably as glass. Transfer to a glass jar within a week or two for best results.",
          },
          {
            q: "Can I store cannabis in the fridge or freezer?",
            a: "Refrigerating is not recommended — temperature and humidity cycles risk moisture damage. Freezing is acceptable only for very long-term storage in a fully airtight, non-crushable container. Thaw slowly and completely before opening.",
          },
        ],
      },
    ],
  },

  // ------------------------------------------------------------------
  {
    slug: "cannabis-delivery-chevy-chase-rockville-potomac-kensington",
    title:
      "Cannabis Delivery Around Bethesda: Serving Chevy Chase, Rockville, Potomac, Kensington & Silver Spring",
    excerpt:
      "A ZIP-by-ZIP look at cannabis delivery in Montgomery County — coverage, timing, ID rules, and what to expect from a local, licensed Maryland dispensary based in Bethesda.",
    date: "2025-09-16",
    readingTime: "6 min",
    tags: ["Local", "Delivery", "Bethesda"],
    image: IMAGES.interior,
    keywords: [
      "cannabis delivery Chevy Chase",
      "weed delivery Rockville MD",
      "Potomac cannabis dispensary",
      "Kensington MD cannabis",
      "Silver Spring dispensary delivery",
      "Montgomery County cannabis",
    ],
    content: [
      {
        type: "p",
        text: "MD Supply Wellness is a Maryland-licensed cannabis dispensary based at 4713 Maple Ave in downtown Bethesda. From our flagship location we're building delivery service across Montgomery County — and this is a quick, honest look at what that means for you if you live in Chevy Chase, Rockville, Potomac, Kensington, or Silver Spring.",
      },
      {
        type: "h2",
        text: "Bethesda 20814, 20815, 20816, 20817",
      },
      {
        type: "p",
        text: "Bethesda is our flagship market. Downtown, Bradley Boulevard, Old Georgetown Road, Wildwood, Sumner, Glen Echo Heights, and Wyngate are all inside our core service zone. Same-day delivery windows will typically run 10 AM – 9 PM at launch, with faster fulfillment for orders placed before 6 PM.",
      },
      {
        type: "h2",
        text: "Chevy Chase 20815 / 20825",
      },
      {
        type: "p",
        text: "Chevy Chase (both the village and the CDP) sits directly on our doorstep — roughly a 5-minute drive from Maple Avenue. Same-day service will be available, and identity verification at the door is the same as it is in Bethesda: valid 21+ ID, matching order name.",
      },
      {
        type: "h2",
        text: "Rockville 20850 / 20852 / 20853",
      },
      {
        type: "p",
        text: "Rockville is a big footprint — from Twinbrook to King Farm to Fallsmead. Expect same-day delivery windows for orders placed by mid-afternoon, and slightly larger delivery windows during peak demand. If you're near a Metro corridor (Rockville, Twinbrook, or Grosvenor), our routing tends to be tight.",
      },
      {
        type: "h2",
        text: "Potomac 20854",
      },
      {
        type: "p",
        text: "Potomac's spread-out neighborhoods — Falls Road, Piney Meetinghouse, River Road — are all inside our delivery footprint. Same-day service will be standard, though larger addresses on the Great Falls end may fall into later windows depending on traffic.",
      },
      {
        type: "h2",
        text: "Kensington 20895",
      },
      {
        type: "p",
        text: "Kensington is one of the fastest runs for us — a short 10-minute drive up Connecticut Avenue. Same-day delivery is standard, and we plan to publish delivery-window updates on our Areas Served page each launch week.",
      },
      {
        type: "h2",
        text: "Silver Spring 20910 / 20901",
      },
      {
        type: "p",
        text: "Silver Spring's inner ZIPs — downtown, Woodside, Forest Glen — are inside our delivery footprint. Silver Spring residents can generally expect afternoon and early-evening windows.",
      },
      {
        type: "h2",
        text: "What every delivery ZIP shares",
      },
      {
        type: "ul",
        items: [
          "Adults 21+ with valid, non-expired government ID.",
          "Or registered Maryland medical patients with a matching MMCC ID.",
          "The named customer must personally receive and sign for the delivery.",
          "All products arrive in sealed, tamper-evident, child-resistant packaging.",
          "Every product carries a batch-level Maryland Certificate of Analysis (COA).",
        ],
      },
      {
        type: "callout",
        text: "Not sure whether your ZIP is inside our footprint? Visit our Areas Served page for a full list. Each ZIP has a dedicated local page with details, delivery timing, and the fastest way to get on the waitlist.",
      },
      {
        type: "h2",
        text: "Delivery FAQ",
      },
      {
        type: "faq",
        items: [
          {
            q: "What's the fastest way to get cannabis delivered in Bethesda?",
            a: "Order online during our business hours and select a same-day window. Most Bethesda addresses will see 60–120 minute fulfillment. Adding your address to our waitlist ensures you're notified the moment we go live at launch.",
          },
          {
            q: "Do you deliver into Washington, DC?",
            a: "DC has a separate cannabis regulatory framework. Maryland-licensed dispensaries can only deliver to Maryland addresses. If you live in a DC border ZIP but list a Maryland delivery address, that address is what matters.",
          },
          {
            q: "How is cannabis delivery different from a normal food delivery?",
            a: "Cannabis is delivered by trained employees of the dispensary — not gig-worker drivers. Every order requires a state-required ID scan and signature at the door, and products stay in tamper-evident packaging until they're inside your home.",
          },
        ],
      },
    ],
  },

  // ------------------------------------------------------------------
  {
    slug: "wellness-dispensary-bethesda-md-faq",
    title:
      "Wellness Dispensary FAQ: What to Expect at MD Supply Wellness in Bethesda, MD",
    excerpt:
      "Quick answers to the questions we hear most from first-time visitors to our wellness dispensary at 4713 Maple Ave in Bethesda, Maryland.",
    date: "2025-09-20",
    readingTime: "3 min",
    tags: ["FAQ", "Wellness Dispensary", "Bethesda"],
    image: IMAGES.interior,
    keywords: [
      "wellness dispensary Bethesda MD",
      "wellness dispensary Maryland",
      "4713 Maple Ave Bethesda",
      "MD Supply Wellness FAQ",
      "cannabis store Bethesda",
    ],
    content: [
      {
        type: "p",
        text: "MD Supply Wellness is a licensed Maryland wellness dispensary at 4713 Maple Ave, Bethesda, MD 20814. If this is your first visit — or your first time at any Maryland dispensary — here are the quick answers to the questions people ask us most.",
      },
      {
        type: "faq",
        items: [
          {
            q: "Where is MD Supply Wellness located?",
            a: "We're at 4713 Maple Ave, Bethesda, MD 20814 — just off Old Georgetown Road in downtown Bethesda, a short walk from the Bethesda Metro station and easy to reach from Chevy Chase, Potomac, Kensington, and Silver Spring.",
          },
          {
            q: "What is a wellness dispensary?",
            a: "A wellness dispensary is a Maryland-licensed retailer that sells cannabis products — flower, vapes, edibles, tinctures, and topicals — alongside guidance focused on well-being rather than just a transaction. Our team is trained to help you choose products that fit your goals, whether that's better sleep, stress relief, or everyday wellness.",
          },
          {
            q: "Who can shop at your Bethesda dispensary?",
            a: "Adults 21 or older with a valid, non-expired government-issued ID, and registered Maryland medical cannabis patients 18+ with a current MMCC patient card plus matching ID.",
          },
          {
            q: "Do I need a medical card to buy cannabis in Maryland?",
            a: "No. Since July 1, 2023, any adult 21+ can purchase cannabis in Maryland. A medical card offers extra benefits — no sales tax, higher purchase limits, and access to patient-only inventory — but it's not required.",
          },
          {
            q: "What products do you carry?",
            a: "A curated selection of Maryland-grown flower, pre-rolls, vape cartridges, edibles, tinctures, capsules, and topicals. Every product is lab-tested and sourced from Maryland-licensed cultivators and processors.",
          },
          {
            q: "Can I just stop by, or do I need an appointment?",
            a: "Walk-ins are welcome during our regular hours. If you want extra time with a wellness consultant, call ahead at (301) 348-1300 and we'll set aside a window for you.",
          },
        ],
      },
      {
        type: "p",
        text: "Still have questions? Call (301) 348-1300 or stop by 4713 Maple Ave in Bethesda. Our team is happy to help.",
      },
    ],
  },

  // ------------------------------------------------------------------
  {
    slug: "wellness-dispensary-maryland-common-questions",
    title:
      "Finding a Wellness Dispensary in Maryland: 7 Common Questions Answered",
    excerpt:
      "Short, practical answers to the questions Maryland residents ask when searching for a wellness dispensary — from legality and ID requirements to what sets a wellness-focused shop apart.",
    date: "2025-09-27",
    readingTime: "3 min",
    tags: ["FAQ", "Maryland Cannabis", "Wellness"],
    image: IMAGES.macro1,
    keywords: [
      "wellness dispensary Maryland",
      "Maryland dispensary FAQ",
      "cannabis Maryland questions",
      "legal cannabis MD",
      "best dispensary Bethesda",
    ],
    content: [
      {
        type: "p",
        text: "Searching for a wellness dispensary in Maryland? Here are seven quick answers that cover almost every question new customers ask us at MD Supply Wellness in Bethesda.",
      },
      {
        type: "faq",
        items: [
          {
            q: "Is cannabis legal in Maryland?",
            a: "Yes. Adult-use cannabis has been legal for adults 21+ in Maryland since July 1, 2023, and the state's medical cannabis program has been operating since 2017. All purchases must be made from a licensed dispensary.",
          },
          {
            q: "What makes a dispensary a 'wellness' dispensary?",
            a: "A wellness dispensary focuses on guidance, education, and matching products to real goals — better sleep, reduced stress, pain relief, mood support — rather than selling the highest-THC product in stock. Our team at 4713 Maple Ave in Bethesda is trained to listen first and recommend second.",
          },
          {
            q: "What do I need to bring with me?",
            a: "A valid, non-expired government ID (driver's license, passport, state ID, or military ID). Medical patients should also bring their MMCC patient card.",
          },
          {
            q: "Do you accept out-of-state IDs?",
            a: "Yes. Any adult 21+ with a valid US government-issued ID can shop under Maryland's adult-use program. Maryland does not currently offer medical reciprocity for out-of-state medical cards.",
          },
          {
            q: "How much can I buy at one time?",
            a: "Adult-use customers can purchase up to 1.5 ounces of flower per day, or the equivalent amount in other product forms. Registered Maryland medical patients have higher allowances tied to their MMCC certification.",
          },
          {
            q: "Do you offer delivery?",
            a: "Yes — we deliver throughout Bethesda, Chevy Chase, Rockville, Potomac, Kensington, Silver Spring, and select DC border ZIP codes. Check our Areas Served page for the full list.",
          },
          {
            q: "What's the fastest way to find your Bethesda dispensary?",
            a: "Search 'MD Supply Wellness' or 'wellness dispensary Bethesda' on Google Maps — we're at 4713 Maple Ave, Bethesda, MD 20814, about a block from the Bethesda Metro station.",
          },
        ],
      },
      {
        type: "p",
        text: "Call us at (301) 348-1300 or visit 4713 Maple Ave, Bethesda, MD 20814 for in-person help choosing the right product.",
      },
    ],
  },

  // ------------------------------------------------------------------
  {
    slug: "visit-md-supply-wellness-4713-maple-ave-bethesda",
    title:
      "Visiting Our Bethesda Wellness Dispensary at 4713 Maple Ave: Everything You Need to Know",
    excerpt:
      "Parking, Metro directions, hours, payment, and what to expect when you walk into MD Supply Wellness at 4713 Maple Ave, Bethesda, MD — the fast FAQ.",
    date: "2025-10-01",
    readingTime: "3 min",
    tags: ["FAQ", "Visit Us", "Bethesda"],
    image: IMAGES.lifestyle,
    keywords: [
      "4713 Maple Ave Bethesda",
      "MD Supply Wellness directions",
      "dispensary Bethesda parking",
      "Bethesda Metro dispensary",
      "wellness dispensary hours Maryland",
    ],
    content: [
      {
        type: "p",
        text: "Planning your first visit to MD Supply Wellness at 4713 Maple Ave in downtown Bethesda? Here's the quick FAQ.",
      },
      {
        type: "faq",
        items: [
          {
            q: "What's your exact address?",
            a: "4713 Maple Ave, Bethesda, MD 20814. We're on Maple Avenue in downtown Bethesda, just off Old Georgetown Road and a short walk from Woodmont Triangle.",
          },
          {
            q: "What are your hours?",
            a: "Monday–Thursday 8:00 AM–10:00 PM, Friday–Saturday 8:00 AM–11:00 PM, Sunday 9:00 AM–9:00 PM.",
          },
          {
            q: "How do I get there by Metro?",
            a: "Take the Red Line to the Bethesda station. We're roughly a 5-minute walk north on Woodmont Avenue, then a short turn onto Maple Avenue.",
          },
          {
            q: "Is parking available?",
            a: "Yes. Metered street parking is available on Maple Ave and surrounding streets, and the Woodmont Avenue and Auburn Avenue public parking garages are both within a block or two.",
          },
          {
            q: "How do you pay at a Maryland dispensary?",
            a: "Most customers pay with cash or a debit-based ATM transaction at checkout. Because cannabis is still federally illegal, standard credit-card payments are limited across the industry. We have an on-site ATM for convenience.",
          },
          {
            q: "Can I bring a friend who's under 21?",
            a: "No. Only adults 21+ (or registered Maryland medical patients 18+ with their MMCC card) may enter the sales floor. Plan accordingly if you're visiting with family.",
          },
          {
            q: "How long should my first visit take?",
            a: "Plan on about 15–20 minutes if you're new. We'll verify your ID, walk you through the menu, answer questions, and help you pick products that fit your goals.",
          },
        ],
      },
      {
        type: "p",
        text: "Questions before you come in? Call (301) 348-1300 or email info@mdsupplywellness.com. We're happy to help.",
      },
    ],
  },

  // ------------------------------------------------------------------
  {
    slug: "maryland-cannabis-wellness-first-visit-faq",
    title:
      "Maryland Cannabis & Wellness FAQ: What First-Time Visitors Ask Us Most",
    excerpt:
      "A short, practical FAQ for anyone brand-new to legal cannabis in Maryland — covering ID, dosing, product choice, and how to shop a wellness dispensary with confidence.",
    date: "2025-10-04",
    readingTime: "3 min",
    tags: ["FAQ", "First Visit", "Beginner"],
    image: IMAGES.macro2,
    keywords: [
      "first time dispensary Maryland",
      "wellness dispensary tips",
      "new to cannabis Maryland",
      "how to shop a dispensary",
      "MD Supply Wellness Bethesda",
    ],
    content: [
      {
        type: "p",
        text: "If this is your first time inside a Maryland wellness dispensary — welcome. These are the questions we hear most often from first-timers at MD Supply Wellness, 4713 Maple Ave, Bethesda, MD.",
      },
      {
        type: "faq",
        items: [
          {
            q: "I've never used cannabis before — where do I start?",
            a: "Start low and slow. For edibles that means 2.5–5 mg of THC and waiting at least two hours. For flower or vape, start with one small puff and wait 10–15 minutes. Our team will walk you through options that match your comfort level.",
          },
          {
            q: "What's the difference between THC and CBD?",
            a: "THC is the compound that produces the classic 'high.' CBD is non-intoxicating and often used for calm, inflammation, or sleep support. Balanced products (like 1:1 CBD:THC) are a popular starting point for new customers.",
          },
          {
            q: "What should I ask the staff when I come in?",
            a: "Tell us your goal (sleep, stress, pain, social use), your experience level, and whether you want something to inhale or to swallow. Those three answers help us narrow the menu quickly.",
          },
          {
            q: "Will the product I buy today be the same next week?",
            a: "Cannabis is a natural product — strain availability, terpene profiles, and potency change batch to batch. If you fall in love with a specific product, ask us to flag it so we can text or email you when it's back in stock.",
          },
          {
            q: "Is it safe to drive after visiting a wellness dispensary?",
            a: "Only if you haven't consumed. Buying cannabis in Maryland is legal; driving while impaired is not. If you plan to try a product the same day, arrange a ride home or use the Bethesda Metro.",
          },
          {
            q: "Can I return a product if I don't like it?",
            a: "Maryland regulations prohibit returns on opened cannabis products. If you have an issue with a sealed product, call (301) 348-1300 and we'll make it right.",
          },
        ],
      },
      {
        type: "p",
        text: "Still have questions before your first visit? Call us at (301) 348-1300 or stop by 4713 Maple Ave, Bethesda, MD 20814. Education is always part of the visit.",
      },
    ],
  },
];

export function getPostBySlug(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug) || null;
}

export function getRelatedPosts(slug, limit = 3) {
  return BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, limit);
}

export function getSortedPosts() {
  return [...BLOG_POSTS].sort((a, b) => (a.date < b.date ? 1 : -1));
}
